import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { CustomerProfile } from '../../models';
import { SmartInputComponent } from '../smart-input/smart-input.component';
import { ZenDialogMsgService } from '../../../zentility/_shared/_services/zen-dialog-msg.service';
import { MatchingService } from '../../services/matching.service';
import { SharedModule } from '../../../zentility/_shared/shared.module';
import { JenZenImgType } from '../../../zentility/_shared/_components/jen-zen-img/jen-zen-img.component';

@Component({
  selector: 'app-customer-review-panel',
  standalone: true,
  imports: [FormsModule, MatCardModule, SmartInputComponent, SharedModule],
  templateUrl: './customer-review-panel.component.html',
  styleUrl: './customer-review-panel.component.scss'
})
export class CustomerReviewPanelComponent implements OnChanges, OnDestroy {
    @Input() profile!: CustomerProfile;
    @Input() isProcessing = false;
    @Input() progress = 0;
    @Output() update = new EventEmitter<CustomerProfile>();

    isEditing = false;
    formData!: CustomerProfile;
    linkedCompanyId = '';
    JenZenImgType = JenZenImgType;

    // Rotating status messages
    statusMessages = [
        'Reading your bill...',
        'Finding important details...',
        'Extracting customer information...',
        'Identifying billing periods...',
        'Calculating usage amounts...',
        'Almost done...'
    ];
    currentStatusMessage = this.statusMessages[0];
    private statusMessageInterval: any;

    constructor(private matchingService: MatchingService, private zenDialogSvc: ZenDialogMsgService) {
        // Init empty default
        this.formData = {
            companyName: '',
            billingAddressStreet1: '',
            billingAddressStreet2: '',
            billingAddressCity: '',
            billingAddressState: '',
            billingAddressZip: '',
            contactFirstName: '',
            contactLastName: '',
            contactEmail: '',
            contactPhone: '',
            isApproved: false
        };
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['profile'] && this.profile) {
            this.formData = { ...this.profile };
        }

        // Start/stop rotating messages based on processing state
        if (changes['isProcessing']) {
            if (this.isProcessing) {
                this.startRotatingMessages();
            } else {
                this.stopRotatingMessages();
            }
        }
    }

    ngOnDestroy() {
        this.stopRotatingMessages();
    }

    private startRotatingMessages() {
        this.stopRotatingMessages(); // Clear any existing interval
        let currentIndex = 0;
        this.currentStatusMessage = this.statusMessages[0];

        this.statusMessageInterval = setInterval(() => {
            currentIndex = (currentIndex + 1) % this.statusMessages.length;
            this.currentStatusMessage = this.statusMessages[currentIndex];
        }, 2500); // Change message every 2.5 seconds
    }

    private stopRotatingMessages() {
        if (this.statusMessageInterval) {
            clearInterval(this.statusMessageInterval);
            this.statusMessageInterval = null;
        }
    }

    handleChange(field: keyof CustomerProfile, value: any) {
        (this.formData as any)[field] = value;
        if (field === 'companyName') this.linkedCompanyId = '';
    }

    async handleLinkCompany(event: {id: string, label: string}) {
        this.linkedCompanyId = event.id;
        this.formData.companyName = event.label;

        const details = await this.matchingService.getMatchDetails(event.id, 'entity');
        if (details && details.billingAddress) {
            this.formData.billingAddressStreet1 = details.billingAddress.street;
            this.formData.billingAddressCity = details.billingAddress.city;
            this.formData.billingAddressState = details.billingAddress.state;
            this.formData.billingAddressZip = details.billingAddress.zip;
        }
    }

    async handleLinkAddress(event: {id: string, label: string}) {
        this.formData.billingAddressStreet1 = event.label;
        const details = await this.matchingService.getMatchDetails(event.id, 'address');
        if (details) {
            this.formData.billingAddressCity = details.city;
            this.formData.billingAddressState = details.state;
            this.formData.billingAddressZip = details.zip;
        }
    }

    handleSave() {
        this.update.emit({ ...this.formData, isApproved: true });
        this.isEditing = false;
    }

    cancelEdit() {
        this.formData = { ...this.profile };
        this.isEditing = false;
    }
}
